/**
 * The worklist's bottom section: problems flagged in the previous run that are
 * clean in this one — proof yesterday's fixes landed, and the reason the queue
 * has a bottom. Visual spec: the approved audit-remodel mockup artifact
 * `.digest` (dashed border, RESOLVED pill, quiet ink).
 */
import React, { useState } from "react"

import { ResolvedProblem } from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"
import { ProblemTypeMap } from "../../../hooks/useProblemTypes"

interface Props {
  resolved: ResolvedProblem[]
  previousRunDate: string | null
  registry: ProblemTypeMap | null
}

export default function ResolvedDigest({ resolved, previousRunDate, registry }: Props) {
  const [expanded, setExpanded] = useState(false)
  if (resolved.length === 0) return null

  const dateLabel = previousRunDate
    ? parseUtcTimestamp(previousRunDate).toLocaleDateString([], { month: "short", day: "numeric" })
    : "the previous run"

  return (
    <div
      style={{
        border: `1px dashed ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.md,
        padding: "12px 16px",
        marginTop: 4,
        fontSize: 13,
        color: THEME.colors.neutral.light.textSecondary,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          background: "none", border: "none", padding: 0, cursor: "pointer",
          fontFamily: THEME.fonts.primary, fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary, textAlign: "left",
        }}
      >
        <span
          style={{
            borderRadius: THEME.borderRadius.pill, fontSize: 10, fontWeight: 700,
            letterSpacing: "0.6px", padding: "2px 8px",
            background: THEME.problemDisplay.severitySoft.expected,
            color: THEME.problemDisplay.severityColors.expected,
          }}
        >
          RESOLVED
        </span>
        {resolved.length.toLocaleString()} since {dateLabel} — {expanded ? "hide" : "show"}
      </button>
      {expanded && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          {resolved.map((entry) => {
            const name =
              [entry.year, entry.make, entry.model]
                .filter((part) => part !== null && `${part}`.trim() !== "")
                .join(" ") || entry.vin
            return (
              <div key={`${entry.vin}-${entry.problem_type}`} style={{ display: "flex", gap: 10, fontSize: 12.5 }}>
                <span style={{ color: THEME.colors.neutral.light.textPrimary, fontWeight: 500 }}>{name}</span>
                <span style={{ fontFamily: THEME.fonts.mono, fontSize: 11.5 }}>{entry.vin}</span>
                <span>{registry?.[entry.problem_type]?.label ?? entry.problem_type}</span>
                {entry.rooftop && <span>{entry.rooftop}</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
